import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import MapPanel from "@/components/MapPanel";
import StatusBadge, { STATUS_COLORS } from "@/components/StatusBadge";
import { MapPin } from "lucide-react";
import { tr } from "@/lib/i18n";

const STATUSES = ["All", "Submitted", "In Review", "Pending", "On Hold", "Approved", "Rejected"];

export default function MapView() {
    const { tr } = useLanguage();
    const [apps, setApps] = useState([]);
    const [status, setStatus] = useState("All");
    const [selectedId, setSelectedId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/applications").then((r) => setApps(r.data)).catch(() => {});
    }, []);

    const filtered = useMemo(() => apps.filter((a) => status === "All" || a.status === status), [apps, status]);
    const selected = apps.find((a) => a.id === selectedId);

    return (
        <div data-testid="map-view-page" className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-heading text-navy dark:text-white">{tr("Map View")}</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{filtered.length} {tr("parcels · colour-coded by status")}</p>
                </div>
                <div className="flex gap-1.5 flex-wrap" data-testid="map-status-filters">
                    {STATUSES.map((s) => (
                        <button key={s} data-testid={`map-filter-${s.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setStatus(s)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${status === s ? "bg-navy text-white dark:bg-teal" : "bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/10"}`}>
                            {s !== "All" && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[s] }} />}
                            {tr(s)}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                <div className="lg:col-span-8">
                    <MapPanel applications={filtered} onSelect={setSelectedId} focusId={selectedId} tools className="h-[560px] card-shadow border border-slate-200/60 dark:border-white/10" />
                </div>
                <div className="lg:col-span-4 space-y-3 max-h-[560px] overflow-y-auto pr-1" data-testid="map-app-list">
                    {filtered.map((a) => (
                        <button key={a.id} data-testid={`map-list-${a.file_number}`} onClick={() => setSelectedId(a.id)}
                            className={`w-full text-left bg-card rounded-[8px] border p-4 card-shadow transition-all hover:card-shadow-hover ${selectedId === a.id ? "border-[#00B4D8] ring-1 ring-[#00B4D8]" : "border-slate-200/60 dark:border-white/10"}`}>
                            <div className="flex items-center justify-between gap-2">
                                <span className="font-data text-[11px] text-teal font-semibold">{a.file_number}</span>
                                <StatusBadge status={a.status} />
                            </div>
                            <div className="mt-1.5 text-sm font-medium text-navy dark:text-slate-100">{a.title}</div>
                            <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" />{a.address}</div>
                        </button>
                    ))}
                </div>
            </div>

            {selected && (
                <div className="glass sticky bottom-4 rounded-[12px] border border-slate-200/60 dark:border-white/10 p-5 card-shadow-hover flex items-center justify-between gap-4 flex-wrap" data-testid="map-selected-panel">
                    <div>
                        <div className="flex items-center gap-2.5">
                            <span className="font-data text-xs text-teal font-semibold">{selected.file_number}</span>
                            <StatusBadge status={selected.status} />
                        </div>
                        <div className="font-heading font-semibold text-navy dark:text-white mt-1">{selected.title}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{selected.address} · {selected.type} · {selected.statistics?.units || 0} units</div>
                    </div>
                    <button data-testid="map-open-file-btn" onClick={() => navigate(`/applications/${selected.id}`)}
                        className="brand-btn px-5 py-2.5 rounded-[6px] text-sm font-semibold">
                        {tr("Open file")}
                    </button>
                </div>
            )}
        </div>
    );
}
